const DEFAULT_API_VERSION = '2025-10';
const WISHLIST_EVENT_NAME = 'wishlist:changed';

function parseJSONScript(element, fallback = null) {
  if (!(element instanceof HTMLScriptElement)) {
    return fallback;
  }

  try {
    return JSON.parse(element.textContent || '');
  } catch (error) {
    console.error('Unable to parse wishlist JSON payload.', error);
    return fallback;
  }
}

function getProductCode(productId) {
  if (typeof productId !== 'string' || !/^gid:\/\/shopify\/Product\/\d+$/.test(productId)) {
    return '';
  }

  return productId.split('/').pop() || '';
}

function extractWishlistCardMarkup(markup, productCode) {
  if (typeof markup !== 'string' || !markup.trim()) {
    return '';
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(markup, 'text/html');
  const section = document.querySelector('#shopify-section-ajax-wishlist-product-card');

  if (!section) {
    return markup;
  }

  section.id = `shopify-section-ajax-wishlist-product-card-${productCode}`;

  return section.innerHTML.trim();
}

if (!customElements.get('user-wishlist')) {
  customElements.define(
    'user-wishlist',
    class UserWishlist extends HTMLElement {
      connectedCallback() {
        if (this.initialized) {
          return;
        }

        this.initialized = true;
        this.grid = this.querySelector('[data-wishlist-grid]');
        this.loading = this.querySelector('[data-wishlist-loading]');
        this.empty = this.querySelector('[data-wishlist-empty]');
        this.error = this.querySelector('[data-wishlist-error]');
        this.messages = parseJSONScript(this.querySelector('[data-user-wishlist-messages]'), {}) || {};
        this.wishlists = parseJSONScript(this.querySelector('[data-user-wishlist-data]'), {}) || {};
        this.shopDomain = this.dataset.shopDomain || window.location.host;
        this.storefrontAccessToken = this.dataset.storefrontAccessToken || '';
        this.apiVersion = this.dataset.apiVersion || DEFAULT_API_VERSION;
        this.isDesignMode = this.dataset.designMode === 'true';
        this.productIds = this.getWishlistProductIds();
        this.handleWishlistChanged = this.handleWishlistChanged.bind(this);

        document.addEventListener(WISHLIST_EVENT_NAME, this.handleWishlistChanged);
        this.loadWishlist();
      }

      disconnectedCallback() {
        document.removeEventListener(WISHLIST_EVENT_NAME, this.handleWishlistChanged);
      }

      getWishlistProductIds() {
        const wishlist = this.wishlists?.default;
        const products = wishlist?.products;

        if (!wishlist || !products) {
          return [];
        }

        const productIds = Array.isArray(products) ? products : Object.values(products);

        return productIds.filter((productId) => getProductCode(productId));
      }

      async loadWishlist() {
        if (this.productIds.length === 0) {
          this.showEmpty();
          return;
        }

        if (!this.storefrontAccessToken) {
          const message = this.isDesignMode ? this.messages.missingToken : this.messages.error;
          this.showError(message);
          return;
        }

        this.showLoading();

        try {
          const products = await this.getProducts(this.productIds);

          if (products.length === 0) {
            this.showEmpty();
            return;
          }

          const markup = await this.renderProductCards(products);

          if (!markup) {
            this.showError(this.messages.error);
            return;
          }

          this.grid.innerHTML = markup;
          this.setVisibleState('grid');
        } catch (error) {
          console.error('Error rendering wishlist.', error);
          this.showError(this.messages.error);
        }
      }

      async getProducts(ids) {
        const query = `
          query WishlistProducts($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on Product {
                id
                handle
              }
            }
          }
        `;
        const payload = await this.makeGraphQLRequest(query, { ids });
        const nodes = payload?.data?.nodes;

        if (!Array.isArray(nodes)) {
          return [];
        }

        const productsById = new Map(
          nodes
            .filter((node) => node && typeof node.id === 'string' && typeof node.handle === 'string')
            .map((node) => [node.id, node])
        );

        return ids.map((productId) => productsById.get(productId)).filter(Boolean);
      }

      async makeGraphQLRequest(query, variables = {}) {
        const response = await fetch(`https://${this.shopDomain}/api/${this.apiVersion}/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken,
          },
          body: JSON.stringify({
            query,
            variables,
          }),
        });

        if (!response.ok) {
          throw new Error(`Wishlist GraphQL request failed with status ${response.status}.`);
        }

        const payload = await response.json();

        if (Array.isArray(payload.errors) && payload.errors.length > 0) {
          throw new Error(payload.errors.map((error) => error.message).join(', '));
        }

        return payload;
      }

      async renderProductCards(products) {
        const cards = await Promise.all(
          products.map(async (product) => {
            const productCode = getProductCode(product.id);

            if (!productCode) {
              return '';
            }

            const response = await fetch(`/products/${product.handle}?section_id=ajax-wishlist-product-card`, {
              credentials: 'same-origin',
            });

            if (!response.ok) {
              throw new Error(`Wishlist card request failed with status ${response.status}.`);
            }

            const markup = await response.text();

            if (!markup.trim()) {
              return '';
            }

            const normalizedMarkup = extractWishlistCardMarkup(markup, productCode);

            if (!normalizedMarkup) {
              return '';
            }

            return `<div class="resource-list__item resource-list__item--wishlist" data-product-id="${productCode}">${normalizedMarkup}</div>`;
          })
        );

        return cards.filter(Boolean).join('');
      }

      handleWishlistChanged(event) {
        const productId = event.detail?.productId;
        const action = event.detail?.action;

        if (!productId || action !== 'remove') {
          return;
        }

        const card = this.querySelector(`[data-product-id="${productId}"]`);

        if (!card) {
          return;
        }

        card.remove();
        this.productIds = this.productIds.filter((id) => getProductCode(id) !== productId);

        if (this.grid && this.grid.children.length === 0) {
          this.showEmpty();
        }
      }

      showLoading() {
        if (this.loading) {
          this.loading.textContent = this.messages.loading || '';
        }

        this.setVisibleState('loading');
      }

      showEmpty() {
        this.setVisibleState('empty');
      }

      showError(message) {
        if (this.error) {
          this.error.textContent = message || this.messages.error || '';
        }

        this.setVisibleState('error');
      }

      setVisibleState(state) {
        if (this.loading) {
          this.loading.hidden = state !== 'loading';
        }

        if (this.empty) {
          this.empty.hidden = state !== 'empty';
        }

        if (this.error) {
          this.error.hidden = state !== 'error';
        }

        if (this.grid) {
          this.grid.hidden = state !== 'grid';
        }
      }
    }
  );
}
